import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, School, Briefcase, Edit2 } from "lucide-react";
import { useState } from "react";
import ProfileEducationForm from "./education/ProfileEducation";
import { format } from "date-fns";

interface Education {
  degree: string;
  institution: string;
  year: string;
}

interface ProfileEducationProps {
  formData: {
    education: Education[];
    _id: string;
  };
  accessToken: string | null;
  freelancerId?: string | null;
}

export const ProfileEducation = ({
  formData,
  accessToken,
  freelancerId,
}: ProfileEducationProps) => {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <Card className="hover:shadow-md transition-all duration-300 border-indigo-100 bg-gradient-to-br from-white to-indigo-50/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-400 to-blue-400 flex items-center justify-center shadow-inner">
            <School className="w-5 h-5 text-white" />
          </div>
          <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
            Education
          </span>
          {!freelancerId &&
          <div
            onClick={() => setIsEditing(!isEditing)}
            className="cursor-pointer"
          >
            <Edit2 className="w-4 h-4" />
          </div>
          }
        </CardTitle>
      </CardHeader>
      {isEditing ? (
        <ProfileEducationForm />
      ) : (
        <CardContent>
          {formData.education.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-gradient-to-br from-indigo-50/50 to-blue-50/50 rounded-lg border border-dashed border-indigo-200 p-6">
              <p className="text-indigo-500 font-medium">
                No education to display.
              </p>
            </div>
          ) : (
            <div className="relative pl-8 space-y-0 pt-2">
              <div className="absolute left-3.5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-indigo-300 to-blue-100"></div>

              {formData.education.map((education, index) => (
                <div
                  key={index}
                  className="relative pb-8 animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div
                    className={`absolute left-[-27px] w-6 h-6 rounded-full bg-blue-50 border flex items-center justify-center shadow-sm z-10`}
                  >
                    <Briefcase className="w-5 h-5 text-blue-500" />
                  </div>

                  <div
                    className={`absolute left-[-12px] top-3 w-2.5 h-0.5 bg-gradient-to-b from-purple-400 to-purple-100`}
                  ></div>

                  <div
                    className={`ml-2 p-4 rounded-lg bg-purple-50 border-purple-100 shadow-sm hover:shadow transition-all`}
                  >
                    <p className="text-gray-800 font-medium">
                      {education?.degree ? education?.degree : ""}
                    </p>
                    <p className="text-gray-600 font-medium">
                      {education?.institution ? education?.institution : ""}
                    </p>
                    {education?.year && (
                      <div className="flex items-center gap-1 mt-2 text-xs">
                        <Calendar className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-gray-600">
                          {education?.year ? format(new Date(education?.year), "yyyy") : "-"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
